"use client";

import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import type {
  AuthUser,
  UserRole,
} from "@/services/domains/identity/auth-types";
import {
  authApiClient,
  getInitialLoadingState,
  INACTIVITY_LOGOUT_MS,
  isPublicRoute,
  sanitizeUserData,
  AUTH_CHECK_TIMEOUT_MS,
  GET_ME_TIMEOUT_MS,
  PROACTIVE_REFRESH_MS,
  REFRESH_TIMEOUT_MS,
  REMEMBER_ME_INACTIVITY_LOGOUT_MS,
} from "@/services/domains/identity";
import { isRememberMeSession } from "@/services/domains/identity/auth-session";
import { toAuthErrorMessage } from "@/services/domains/identity/auth-errors";
import { TokenStorage } from "@/services/shared/infrastructure/storage";
import { AuthContext } from "../contexts/AuthContext";

const apiClient = authApiClient;

const MOUSE_THROTTLE_MS = 30_000;
const ACTIVITY_EVENTS: (keyof WindowEventMap)[] = [
  "keydown",
  "keypress",
  "click",
  "mousedown",
  "scroll",
  "touchstart",
];

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [authCheckComplete, setAuthCheckComplete] = useState(false);
  const [loading, setLoading] = useState(getInitialLoadingState);
  const navigate = useNavigate();

  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const expirationCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef(true);
  const refreshPromiseRef = useRef<Promise<void> | null>(null);
  const inactivityTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const lastMouseMoveRef = useRef<number>(0);
  const hasCheckedAuthRef = useRef(false);

  const stopTokenRefresh = useCallback(() => {
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
    }
    if (expirationCheckIntervalRef.current) {
      clearInterval(expirationCheckIntervalRef.current);
      expirationCheckIntervalRef.current = null;
    }
  }, []);

  const refreshTokenSafely = useCallback(async (): Promise<void> => {
    if (refreshPromiseRef.current) return refreshPromiseRef.current;
    refreshPromiseRef.current = (async () => {
      try {
        await apiClient.refreshToken();
      } finally {
        refreshPromiseRef.current = null;
      }
    })();
    return refreshPromiseRef.current;
  }, []);

  const startTokenRefresh = useCallback(() => {
    if (!apiClient.isAuthenticated()) return;
    stopTokenRefresh();

    expirationCheckIntervalRef.current = setInterval(() => {
      if (!isMountedRef.current) return;
      const hasAccessToken = apiClient.isAuthenticated();
      const hasRefreshToken = !!TokenStorage.get("refresh_token");

      if (!hasAccessToken && !hasRefreshToken) {
        stopTokenRefresh();
        apiClient.logout();
        setUser(null);
        navigate(
          "/login?error=session_expired&message=" +
            encodeURIComponent("Your session has expired. Please login again."),
        );
        return;
      }

      if (!hasAccessToken && hasRefreshToken) {
        refreshTokenSafely().catch(() => {
          stopTokenRefresh();
          if (isMountedRef.current) {
            apiClient.logout();
            setUser(null);
            navigate(
              "/login?error=session_expired&message=" +
                encodeURIComponent(
                  "Your session has expired. Please login again.",
                ),
            );
          }
        });
      }
    }, 30 * 1000);

    refreshIntervalRef.current = setInterval(
      async () => {
        if (!apiClient.isAuthenticated() || !isMountedRef.current) {
          stopTokenRefresh();
          return;
        }
        try {
          await refreshTokenSafely();
          try {
            const response = await apiClient.getMe();
            const sanitizedUser = sanitizeUserData(response.data);
            if (sanitizedUser && isMountedRef.current) {
              setUser(sanitizedUser);
            }
          } catch {
            // getMe failed after refresh – token is valid, don't logout
          }
        } catch {
          stopTokenRefresh();
          if (isMountedRef.current) {
            apiClient.logout();
            setUser(null);
            navigate("/login?error=session_expired");
          }
        }
      },
      PROACTIVE_REFRESH_MS,
    );
  }, [stopTokenRefresh, navigate, refreshTokenSafely]);

  const checkAuth = useCallback(async () => {
    const checkId = Math.random().toString(36).substring(7);
    let timeoutId: NodeJS.Timeout | null = null;
    let loadingSet = false;

    const setLoadingFalse = () => {
      if (!loadingSet && isMountedRef.current) {
        loadingSet = true;
        setLoading(false);
        setAuthCheckComplete(true);
      }
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    if (typeof window !== "undefined") {
      const pathname = window.location.pathname;
      if (isPublicRoute(pathname)) {
        setLoadingFalse();
        return;
      }
    } else {
      setLoadingFalse();
      return;
    }

    timeoutId = setTimeout(() => {
      setLoadingFalse();
    }, AUTH_CHECK_TIMEOUT_MS);

    try {
      const hasAccessToken = apiClient.isAuthenticated();
      const hasRefreshToken = !!TokenStorage.get("refresh_token");

      if (!hasAccessToken && !hasRefreshToken) {
        setLoadingFalse();
        return;
      }

      if (!hasAccessToken && hasRefreshToken) {
        try {
          await refreshTokenSafely();
        } catch {
          apiClient.logout();
          setUser(null);
          setLoadingFalse();
          return;
        }
      }

      try {
        const getMePromise = apiClient.getMe();
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error("getMe timeout")), GET_ME_TIMEOUT_MS),
        );

        const response = (await Promise.race([
          getMePromise,
          timeoutPromise,
        ]).catch((error) => {
          throw { shouldRefresh: true, originalError: error };
        })) as any;

        const sanitizedUser = sanitizeUserData(response.data);

        if (sanitizedUser && isMountedRef.current) {
          setUser(sanitizedUser);
          setAuthCheckComplete(true);
          startTokenRefresh();
          setLoadingFalse();
          return;
        }

        setUser(null);
        apiClient.logout();
        setLoadingFalse();
        return;
      } catch (error: any) {
        if (
          error.shouldRefresh &&
          error.originalError?.message !== "getMe timeout"
        ) {
          try {
            const refreshPromise = refreshTokenSafely();
            const refreshTimeoutPromise = new Promise((_, reject) =>
              setTimeout(() => reject(new Error("refresh timeout")), REFRESH_TIMEOUT_MS),
            );
            await Promise.race([refreshPromise, refreshTimeoutPromise]);

            const getMePromise = apiClient.getMe();
            const getMeTimeoutPromise = new Promise((_, reject) =>
              setTimeout(() => reject(new Error("getMe timeout")), GET_ME_TIMEOUT_MS),
            );
            const response = (await Promise.race([
              getMePromise,
              getMeTimeoutPromise,
            ])) as any;

            const sanitizedUser = sanitizeUserData(response.data);

            if (sanitizedUser && isMountedRef.current) {
              setUser(sanitizedUser);
              setAuthCheckComplete(true);
              startTokenRefresh();
              setLoadingFalse();
              return;
            }

            setUser(null);
            apiClient.logout();
            setAuthCheckComplete(true);
            setLoadingFalse();
            return;
          } catch {
            apiClient.logout();
            if (isMountedRef.current) {
              setUser(null);
              setAuthCheckComplete(true);
            }
            setLoadingFalse();
            return;
          }
        }
        setLoadingFalse();
        return;
      }
    } catch {
      apiClient.logout();
      if (isMountedRef.current) {
        setUser(null);
        setAuthCheckComplete(true);
      }
      setLoadingFalse();
    } finally {
      setLoadingFalse();
    }
  }, [startTokenRefresh, refreshTokenSafely]);

  const logout = useCallback(() => {
    if (inactivityTimeoutRef.current) {
      clearTimeout(inactivityTimeoutRef.current);
      inactivityTimeoutRef.current = null;
    }
    stopTokenRefresh();
    apiClient.logout();
    setUser(null);

    if (typeof window !== "undefined") {
      localStorage.removeItem("sidenav_collapsed");
      localStorage.removeItem("sidenav_locked");
    }

    if (typeof window !== "undefined") {
      const logoutTimestamp = Date.now();
      localStorage.setItem("logout", JSON.stringify(logoutTimestamp));
      setTimeout(() => {
        try {
          const stored = localStorage.getItem("logout");
          if (stored && JSON.parse(stored) === logoutTimestamp) {
            localStorage.removeItem("logout");
          }
        } catch {
          // Ignore
        }
      }, 5000);
    }

    navigate("/login", { replace: true });
  }, [stopTokenRefresh, navigate]);

  // Multi-tab session sync
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "logout") {
        try {
          const logoutTimestamp = e.newValue ? JSON.parse(e.newValue) : null;
          if (logoutTimestamp && typeof logoutTimestamp === "number") {
            const age = Date.now() - logoutTimestamp;
            if (age < 5000) {
              stopTokenRefresh();
              setUser(null);
              navigate("/login");
            }
          } else if (e.newValue === "true") {
            stopTokenRefresh();
            setUser(null);
            navigate("/login");
          }
        } catch {
          // Ignore
        }
      }
      if (e.key === "auth_token" && !e.newValue) {
        stopTokenRefresh();
        setUser(null);
      }
    };

    const handleAuthExpired = (e: CustomEvent) => {
      const error = e.detail?.error || "Your session has expired";
      stopTokenRefresh();
      apiClient.logout();
      setUser(null);
      navigate(
        `/login?error=session_expired&message=${encodeURIComponent(error)}`,
      );
    };

    const handleAuthUnauthorized = (e: CustomEvent) => {
      const error = e.detail?.error || "Unauthorized";
      const pathname =
        typeof window !== "undefined" ? window.location.pathname : "";
      if (pathname === "/login" || pathname?.startsWith("/login")) return;
      stopTokenRefresh();
      apiClient.logout();
      setUser(null);
      navigate(
        `/login?error=unauthorized&message=${encodeURIComponent(error)}`,
      );
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("auth:expired", handleAuthExpired as EventListener);
    window.addEventListener(
      "auth:unauthorized",
      handleAuthUnauthorized as EventListener,
    );

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener(
        "auth:expired",
        handleAuthExpired as EventListener,
      );
      window.removeEventListener(
        "auth:unauthorized",
        handleAuthUnauthorized as EventListener,
      );
    };
  }, [stopTokenRefresh, navigate]);

  // Inactivity logout
  useEffect(() => {
    if (!user || typeof window === "undefined") return;

    const inactivityMs = isRememberMeSession()
      ? REMEMBER_ME_INACTIVITY_LOGOUT_MS
      : INACTIVITY_LOGOUT_MS;

    const scheduleLogout = () => {
      if (inactivityTimeoutRef.current)
        clearTimeout(inactivityTimeoutRef.current);
      inactivityTimeoutRef.current = setTimeout(() => {
        if (isMountedRef.current) logout();
      }, inactivityMs);
    };

    const onActivity = () => scheduleLogout();

    const onMouseMove = () => {
      const now = Date.now();
      if (now - lastMouseMoveRef.current >= MOUSE_THROTTLE_MS) {
        lastMouseMoveRef.current = now;
        onActivity();
      }
    };

    scheduleLogout();

    ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, onActivity));
    window.addEventListener("mousemove", onMouseMove);

    return () => {
      ACTIVITY_EVENTS.forEach((ev) =>
        window.removeEventListener(ev, onActivity),
      );
      window.removeEventListener("mousemove", onMouseMove);
      if (inactivityTimeoutRef.current) {
        clearTimeout(inactivityTimeoutRef.current);
        inactivityTimeoutRef.current = null;
      }
    };
  }, [user, logout]);

  // Initial auth run (once on mount)
  useEffect(() => {
    const pathname =
      typeof window !== "undefined" ? window.location.pathname : "";

    if (hasCheckedAuthRef.current) return;

    isMountedRef.current = true;

    if (typeof window !== "undefined") {
      if (isPublicRoute(pathname)) {
        setLoading(false);
        setAuthCheckComplete(true);
        hasCheckedAuthRef.current = true;
        return;
      }
    }

    const hasAccessToken = apiClient.isAuthenticated();
    const hasRefreshToken = !!TokenStorage.get("refresh_token");

    if (hasAccessToken || hasRefreshToken) {
      (async () => {
        try {
          if (!hasAccessToken && hasRefreshToken) {
            await refreshTokenSafely();
          }
          const response = await apiClient.getMe();
          const sanitizedUser = sanitizeUserData(response.data);

          if (sanitizedUser && isMountedRef.current) {
            setUser(sanitizedUser);
            setAuthCheckComplete(true);
            setLoading(false);
            startTokenRefresh();
          }
        } catch {
          // Fall through to checkAuth
        }
      })();
    }

    hasCheckedAuthRef.current = true;
    void checkAuth();

    return () => {
      isMountedRef.current = false;
      hasCheckedAuthRef.current = false;
      stopTokenRefresh();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (
    email: string,
    password: string,
    rememberMe: boolean = false,
  ) => {
    try {
      const response = await apiClient.login(email, password, rememberMe);

      const userDataForSanitization = {
        orgIds: response.data.orgIds,
        userId: response.data.userId,
        email: response.data.email,
        role: response.data.role,
      };

      const sanitizedUser = sanitizeUserData(userDataForSanitization);

      if (!sanitizedUser) {
        throw new Error("Invalid user data received from server");
      }

      setUser(sanitizedUser);
      startTokenRefresh();
    } catch (error) {
      throw new Error(toAuthErrorMessage(error));
    }
  };

  const refreshToken = async () => {
    try {
      await refreshTokenSafely();
      const response = await apiClient.getMe();
      const sanitizedUser = sanitizeUserData(response.data);
      if (sanitizedUser) {
        setUser(sanitizedUser);
      } else {
        throw new Error("Invalid user data after refresh");
      }
    } catch (error) {
      logout();
      throw error;
    }
  };

  const hasRole = (role: UserRole): boolean => {
    if (!user?.role) return false;
    const roleHierarchy: Record<UserRole, number> = {
      platform_admin: 4,
      org_admin: 3,
      user: 2,
      viewer: 1,
    };
    const userRole = user.role as UserRole;
    return roleHierarchy[userRole] >= roleHierarchy[role];
  };

  const isPlatformAdmin = (): boolean => {
    if (!user?.email) return false;
    const platformAdminEmails = [
      "nasser.rahal@fazezero.com",
      "info@fazezero.com",
    ];
    return (
      user.role === "platform_admin" ||
      platformAdminEmails.includes(user.email.toLowerCase())
    );
  };

  const isAuthenticated = !!user;

  return (
    <AuthContext.Provider
      value={{
        user,
        isPending: loading,
        login,
        logout,
        isAuthenticated,
        hasRole,
        refreshToken,
        isPlatformAdmin,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
